import { Box, Stack } from "@mui/material";
import { Card, Typography } from "@platform/design-system";

import type {
  CarrierCommentDocument,
  PlanGroupSection,
} from "./buildCommentDocument";

interface CommentGroupListProps {
  carrier: CarrierCommentDocument;
  setSectionRef: (el: HTMLDivElement | null, id: string) => void;
}

const PlanGroupCard = ({
  section,
  showLabel,
}: {
  section: PlanGroupSection;
  showLabel: boolean;
}): React.JSX.Element => (
  <Card
    variant="outlined"
    padding="normal"
    headerDivider={false}
    title={
      showLabel ? (
        <Typography
          variant="body-medium"
          color="textPrimary"
          component="h2"
          sx={{ fontWeight: 700 }}
        >
          {section.label}
        </Typography>
      ) : undefined
    }
    data-testid={`needs-review-comments-plan-group-${section.key}`}
  >
    <Box component="ul" sx={{ listStyleType: "disc", m: 0, pl: 2.5 }}>
      {section.bullets.map((bullet) => (
        <Box
          component="li"
          key={bullet.id}
          sx={{
            "&::marker": { color: "brandPrimary.main" },
            "&:not(:last-of-type)": { mb: 1.5 },
          }}
        >
          <Typography
            variant="subtitle2"
            color="textPrimary"
            component="p"
            sx={{ m: 0 }}
          >
            {bullet.benefitLabel}
          </Typography>
          <Box
            component="ul"
            sx={{ listStyleType: "circle", m: 0, mt: 0.25, pl: 2.5 }}
          >
            <Box component="li">
              <Typography
                variant="body-medium"
                color="textSecondary"
                component="p"
                sx={{ m: 0 }}
              >
                {bullet.comment}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  </Card>
);

const Section = ({
  id,
  heading,
  planGroups,
  showPlanGroupHeaders,
  setSectionRef,
}: {
  id: string;
  heading: string;
  planGroups: readonly PlanGroupSection[];
  showPlanGroupHeaders: boolean;
  setSectionRef: (el: HTMLDivElement | null, sectionId: string) => void;
}): React.JSX.Element => (
  <Box
    ref={(el: HTMLDivElement | null): void => setSectionRef(el, id)}
    data-testid={`needs-review-comments-section-${id}`}
  >
    <Typography color="textPrimary" sx={{ mb: 1, fontWeight: 600 }}>
      {heading}
    </Typography>
    <Stack spacing={2} sx={{ mb: 3 }}>
      {planGroups.map((planGroup) => (
        <PlanGroupCard
          key={planGroup.key}
          section={planGroup}
          showLabel={showPlanGroupHeaders}
        />
      ))}
    </Stack>
  </Box>
);

/**
 * Renders one carrier's Needs Review comments: coverage blocks (Quotes/SBS)
 * in canonical order, then a flat Comps section, then a flat Responses
 * section, per the user story's acceptance criteria. Each top-level section
 * registers itself with `AnchorNav` via `setSectionRef` so the drawer's
 * right-hand nav can scroll to it.
 */
const CommentGroupList = ({
  carrier,
  setSectionRef,
}: CommentGroupListProps): React.JSX.Element => (
  <Box data-testid="needs-review-comments-body" sx={{ px: 2, py: 2 }}>
    {carrier.coverages.map((coverage) => (
      <Section
        key={coverage.coverageType}
        id={coverage.coverageType}
        heading={coverage.heading}
        planGroups={coverage.planGroups}
        showPlanGroupHeaders={coverage.showPlanGroupHeaders}
        setSectionRef={setSectionRef}
      />
    ))}
    {carrier.comps.length > 0 && (
      <Section
        id="comps"
        heading="Comps"
        planGroups={carrier.comps}
        showPlanGroupHeaders
        setSectionRef={setSectionRef}
      />
    )}
    {carrier.responses.length > 0 && (
      <Section
        id="responses"
        heading="Responses"
        planGroups={carrier.responses}
        showPlanGroupHeaders
        setSectionRef={setSectionRef}
      />
    )}
  </Box>
);

export default CommentGroupList;
